import React, { useState, useMemo, useRef, useEffect } from 'react';
import './ConstellationTimeline.css';
import { useDispatch, useSelector } from 'react-redux';
import SatelliteIndicator from "../../../assets/images/svgs/SatelliteIndicator.svg?react"
import DashedSatelliteIcon from "../../../assets/images/svgs/DashedSatelliteIcon.svg?react"
import { highlightOrbitpath, setProvider } from '../../../slices/orderSlice';

const parseISOTime = (isoString) => {
    return new Date(isoString).getTime() / 1000;
};

const formatISOString = (epochSeconds) => {
    const date = new Date(epochSeconds * 1000);
    return date.toISOString();
};

// hourly weather timestamps may come without a tz suffix; treat them as UTC
const parseWeatherTime = (t) => parseISOTime(/(Z|[+-]\d{2}:?\d{2})$/.test(t) ? t : `${t}Z`);

// cloud % of the forecast hour closest to the pass midpoint, null if outside the forecast window
const getCloudForPass = (pass, hourly) => {
    if (!hourly.length) return null;
    const mid = (parseISOTime(pass.entryTime) + parseISOTime(pass.exitTime)) / 2;
    let best = null;
    let bestDiff = Infinity;
    hourly.forEach(h => {
        const diff = Math.abs(h.time - mid);
        if (diff < bestDiff) {
            bestDiff = diff;
            best = h;
        }
    });
    return bestDiff <= 3600 ? best.cloud : null;
};

const getTimeLabels = (startTime, endTime, pixelsPerSecond) => {
    const labels = [];

    let interval;
    if (pixelsPerSecond > 0.5) {
        interval = 600;
    } else if (pixelsPerSecond > 0.1) {
        interval = 1800;
    } else if (pixelsPerSecond > 0.05) {
        interval = 3600;
    } else {
        interval = 7200;
    }

    const firstLabel = Math.ceil(startTime / interval) * interval;

    for (let time = firstLabel; time <= endTime; time += interval) {
        labels.push({
            time,
            position: (time - startTime) * pixelsPerSecond,
            label: formatISOString(time)
        });
    }

    return labels;
};

const formatPassTime = (isoString) => `${new Date(isoString).toISOString().slice(11, 16)} UTC`;

const CloudIcon = () => (
    <svg className="tooltip-cloud-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 18a4 4 0 0 1-.6-7.96A6 6 0 0 1 18 9.5a4.25 4.25 0 0 1-.5 8.5H7z" />
    </svg>
);

const OpportunityCard = ({ item, cloudCoverMax, weatherNote }) => {
    if (!item) {
        return (
            <div className="opportunity-card opportunity-card-empty">
                Select a pass to see details
            </div>
        );
    }

    const { pass, provider, cloudCover } = item;
    const hasCloud = cloudCover != null;
    const isAboveLimit = hasCloud && cloudCoverMax != null && cloudCover > cloudCoverMax;
    const status = !hasCloud ? 'none' : isAboveLimit ? 'above' : 'within';
    const statusLabel = { none: 'No Forecast', above: 'Above Limit', within: 'Within Limit' }[status];

    return (
        <div className={`opportunity-card status-${status}`}>
            <div className="opportunity-name" title={provider.providerName}>{provider.providerName}</div>
            <div className="opportunity-meta">
                <span>{new Date(pass.entryTime).toISOString().slice(0, 10)}</span>
                <span className="opportunity-divider" />
                <span>{formatPassTime(pass.entryTime)}</span>
            </div>
            <div className="opportunity-cloud">
                <span className="opportunity-cloud-value">{hasCloud ? `${Math.round(cloudCover)}%` : '--'}</span>
                <span className="opportunity-cloud-label">Cloud Coverage</span>
            </div>
            <div className="opportunity-status">{statusLabel}</div>
            {weatherNote && <div className="opportunity-note">{weatherNote}</div>}
        </div>
    );
};

const SatellitePassMarker = ({ 
    pass, 
    provider, 
    startTime, 
    pixelsPerSecond, 
    onClick, 
    isSelected, 
    isInShowOrbit, 
    isHighlighted,
    cloudCover,
    cloudCoverMax,
    markerRef 
}) => {
    const [isHovered, setIsHovered] = useState(false);

    const entryEpoch = parseISOTime(pass.entryTime);
    const exitEpoch = parseISOTime(pass.exitTime);

    const left = (entryEpoch - startTime) * pixelsPerSecond;

    let markerClass = 'satellite-pass-marker';
    if (isSelected) markerClass += ' selected';
    if (isHighlighted) markerClass += ' highlighted';
    if (isInShowOrbit && !isHighlighted) markerClass += ' in-show-orbit';

    let IconComponent = DashedSatelliteIcon;

    if (isHighlighted) {
        IconComponent = SatelliteIndicator;
    } else if (isInShowOrbit && !isHighlighted) {
        IconComponent = SatelliteIndicator
    }

    const hasCloud = cloudCover != null;
    const isAboveLimit = hasCloud && cloudCoverMax != null && cloudCover > cloudCoverMax;
    if (hasCloud) markerClass += isAboveLimit ? ' cloud-above' : ' cloud-within';

    return (
        <div
            ref={markerRef}
            className={markerClass}
            style={{ left: `${left}px` }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => onClick(pass, provider)}
        >
            {hasCloud ? (
                <div className="cloud-capsule">
                    <div className="cloud-capsule-fill" style={{ height: `${Math.min(100, Math.max(0, cloudCover))}%` }} />
                </div>
            ) : (
                <div className="satellite-icon">
                    <IconComponent />
                </div>
            )}
            {(isHovered ) && (
                <div className="satellite-tooltip">
                    {hasCloud && (
                        <>
                            <CloudIcon />
                            <span className="tooltip-cloud">{Math.round(cloudCover)}%</span>
                            <span className="tooltip-divider" />
                        </>
                    )}
                    <span className="tooltip-time">{formatPassTime(pass.entryTime)}</span>
                </div>
            )}
        </div>
    );
};

const ConstellationTimeline = ({ cloudCoverMax }) => {
    const dispatch = useDispatch();
    const [selectedPass, setSelectedPass] = useState(null);
    const scrollContainerRef = useRef(null);
    const highlightedMarkerRef = useRef(null);
    
    const providerConstellations = useSelector(state => state.orderDetails.providerConstellations);
    const showOrbit = useSelector(state => state.orderDetails.showOrbit);
    const highlightOrbit = useSelector(state => state.orderDetails.highlightOrbit);

    const weatherForecast = useSelector(state => state.orderDetails.weatherForecast);
    const weatherLoading = useSelector(state => state.orderDetails.weatherLoading);
    const weatherError = useSelector(state => state.orderDetails.weatherError);

    let weatherNote = null;
    if (weatherLoading) weatherNote = 'Loading cloud data...';
    else if (weatherError) weatherNote = 'Cloud data unavailable';
    else if (weatherForecast?.isFallback) weatherNote = 'Cloud % at AOI centre (area too large for grid)';

    const pixelsPerSecond = 0.2;

    const hourlyCloud = useMemo(() => (
        (weatherForecast?.hourly || [])
            .filter(h => h?.time && h.cloud_cover != null)
            .map(h => ({ time: parseWeatherTime(h.time), cloud: h.cloud_cover }))
    ), [weatherForecast]);

    const { startTime, endTime } = useMemo(() => {
        if (!providerConstellations || providerConstellations.length === 0) {
            return { startTime: 0, endTime: 0 };
        }

        let minTime = Infinity;
        let maxTime = -Infinity;

        providerConstellations.forEach(provider => {
            provider.satellitePasses.forEach(pass => {
                const entry = parseISOTime(pass.entryTime);
                const exit = parseISOTime(pass.exitTime);
                minTime = Math.min(minTime, entry);
                maxTime = Math.max(maxTime, exit);
            });
        });

        const padding = 3600;
        return {
            startTime: minTime - padding,
            endTime: maxTime + padding
        };
    }, [providerConstellations]);

    const duration = endTime - startTime;
    const timelineWidth = duration * pixelsPerSecond;

    useEffect(() => {
        if (highlightOrbit && highlightedMarkerRef.current && scrollContainerRef.current) {
            
            const markerLeft = highlightedMarkerRef?.current?.offsetLeft;
            const containerWidth = scrollContainerRef?.current?.clientWidth;
            
            const scrollPosition = markerLeft - (containerWidth / 2);
            
            scrollContainerRef?.current?.scrollTo({
                left: Math.max(0, scrollPosition),
                behavior: 'smooth'
            });
        }
    }, [highlightOrbit]);

    const allPasses = useMemo(() => {
        if (!providerConstellations || providerConstellations.length === 0) {
            return [];
        }

        const passes = [];
        
        providerConstellations.forEach(provider => {
            provider.satellitePasses.forEach(pass => {
                const isInShowOrbit = showOrbit && 
                    showOrbit.providerID === provider.providerID &&
                    showOrbit.satellitePasses.some(sp => sp.entryTime === pass.entryTime);
                
                const isHighlighted = highlightOrbit && 
                    highlightOrbit.entryTime === pass.entryTime;
                
                passes.push({ 
                    pass, 
                    provider,
                    cloudCover: getCloudForPass(pass, hourlyCloud),
                    isInShowOrbit,
                    isHighlighted
                });
            });
        });
        
        return passes;
    }, [providerConstellations, showOrbit, highlightOrbit, hourlyCloud]);

    const highlightedItem = allPasses.find(item => item.isHighlighted) || null;

    const handlePassClick = (pass, provider) => {
        dispatch(highlightOrbitpath(pass));
        dispatch(setProvider(provider))        
        setSelectedPass({ pass, provider });
    };

    const timeLabels = getTimeLabels(startTime, endTime, pixelsPerSecond);

    if (!providerConstellations || providerConstellations.length === 0) {
        return (
            <div className="timeline-scroll-container">
                <div className="timeline-empty-state">No satellite passes available</div>
            </div>
        );
    }

    return (
        <div className="constellation-timeline">
        <div ref={scrollContainerRef} className="timeline-scroll-container">
            <div className="timeline-content" style={{ width: `${timelineWidth}px` }}>
                {timeLabels.map((label, idx) => (
                    <div
                        key={idx}
                        className="time-label"
                        style={{ left: `${label.position}px` }}
                    >
                        <div className="time-tick" />
                        <div className="time-text">{label.label}</div>
                    </div>
                ))}

                {allPasses.map((item, idx) => (
                    <SatellitePassMarker
                        key={`${item.provider.providerID}-${item.pass.entryTime}-${idx}`}
                        pass={item.pass}
                        provider={item.provider}
                        startTime={startTime}
                        pixelsPerSecond={pixelsPerSecond}
                        onClick={handlePassClick}
                        isSelected={selectedPass?.pass.entryTime === item.pass.entryTime}
                        isInShowOrbit={item.isInShowOrbit}
                        isHighlighted={item.isHighlighted}
                        cloudCover={item.cloudCover}
                        cloudCoverMax={cloudCoverMax}
                        markerRef={item.isHighlighted ? highlightedMarkerRef : null}
                    />
                ))}
            </div>
        </div>
        <OpportunityCard item={highlightedItem} cloudCoverMax={cloudCoverMax} />
        </div>
    );
};

export default ConstellationTimeline;